import { Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import SessionManager from 'components/SessionManager'
import { useApp, useTheme } from 'hooks'
import AppLayout from 'layouts/AppLayout'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { ToastContainer } from 'react-toastify'
import Loader from 'ts/components/Loader'
import NotFound from 'ts/modules/NotFound'
import ProtectedRoute from './components/ProtectedRoute'
import AuthLayout from './layouts/AuthLayout'
import 'react-toastify/dist/ReactToastify.css'
import './assets/styles/main.scss'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from 'routes'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
})

function App() {
  const { isAppReady, isLoading } = useApp()
  const { theme } = useTheme()

  if (!isAppReady) {
    return <Loader message="Loading app data..." />
  }

  return (
    <QueryClientProvider client={queryClient}>
      {isLoading && <Loader message="Loading..." />}
      <ToastContainer hideProgressBar theme={theme === 'dark' ? 'dark' : 'light'} />
      <BrowserRouter>
        <SessionManager />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route element={<AuthLayout />}>
              {Object.values(PUBLIC_ROUTES).map((route) => (
                <Route key={route.path} path={route.path} element={<route.component />} />
              ))}
            </Route>

            {/* Protected routes - requires authentication */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route index element={<Navigate to={PRIVATE_ROUTES.DASHBOARD.path} replace />} />
                {Object.values(PRIVATE_ROUTES).map((route) => (
                  <Route
                    key={route.path}
                    path={route.children ? `${route.path}/*` : route.path}
                    element={<route.component />}
                  />
                ))}
              </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
